import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../components/Toast'
import { canModerate } from '../lib/supabase'
import { awardPostCI } from '../lib/useColumnInches'

export default function ModerationPage() {
  const { profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [comments, setComments] = useState([])
  const [posts, setPosts] = useState([])
  const [tab, setTab] = useState('comments')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return
    if (!profile) return
    if (!canModerate(profile?.role)) { navigate('/'); return }
    fetchAll()
  }, [authLoading, profile?.role])

  async function fetchAll() {
    setLoading(true)
    const [c, p] = await Promise.all([
      supabase.from('comments').select('*, profiles!comments_author_id_fkey(display_name), posts(title, id)').order('created_at', { ascending: false }).limit(100),
      supabase.from('posts').select('*, profiles!posts_author_id_fkey(display_name)').in('status', ['published', 'pending']).order('created_at', { ascending: false }).limit(100)
    ])
    setComments(c.data || [])
    setPosts(p.data || [])
    setLoading(false)
  }

  async function deleteComment(id) {
    if (!window.confirm('Delete this comment?')) return
    await supabase.from('comments').delete().eq('id', id)
    setComments(prev => prev.filter(x => x.id !== id))
    toast('Comment deleted.', 'success')
  }

  async function deletePost(id) {
    if (!window.confirm('Remove this post?')) return
    await supabase.from('posts').update({ status: 'rejected' }).eq('id', id)
    setPosts(prev => prev.filter(x => x.id !== id))
    toast('Post removed from public view.', 'success')
  }

  async function toggleFeatured(post) {
    await supabase.from('posts').update({ featured: !post.featured }).eq('id', post.id)
    toast(post.featured ? 'Removed from featured.' : 'Set as featured piece.', 'success')
    setPosts(prev => prev.map(x => x.id === post.id ? { ...x, featured: !post.featured } : x))
  }

  async function publishPost(id) {
    await supabase.from('posts').update({ status: 'published' }).eq('id', id)
    toast('Post published.', 'success')
    const { data: post } = await supabase.from('posts').select('author_id, section, body, title').eq('id', id).single()
    if (post?.author_id) {
      awardPostCI(post.author_id, post).catch(console.error)
      supabase.from('notifications').insert({
        user_id: post.author_id, actor_id: null, post_id: id,
        type: 'published', message: `"${post.title}" has been published on The Margins.`,
      }).catch(console.error)
    }
    setPosts(prev => prev.map(x => x.id === id ? { ...x, status: 'published' } : x))
  }

  async function rejectPost(id) {
    const note = window.prompt('Optional: leave a note for the author (they will see this in their dashboard):')
    if (note === null) return
    await supabase.from('posts').update({ status: 'rejected', rejection_note: note.trim() || null }).eq('id', id)
    toast('Post rejected.', 'success')
    const { data: post } = await supabase.from('posts').select('author_id, title').eq('id', id).single()
    if (post?.author_id) {
      supabase.from('notifications').insert({
        user_id: post.author_id, actor_id: null, post_id: id, type: 'rejected',
        message: note.trim() ? `"${post.title}" was not accepted. Editor's note: ${note.trim()}` : `"${post.title}" was not accepted this time.`,
      }).catch(console.error)
    }
    setPosts(prev => prev.map(x => x.id === id ? { ...x, status: 'rejected' } : x))
  }

  if (authLoading) return <div className="loading-screen"><div className="loading-logo">The Margins<span className="loading-logo-dot">.</span></div></div>
  if (!canModerate(profile?.role)) return null

  return (
    <div className="moderation-page">
      <div className="moderation-header">
        <div>
          <p style={styles.eyebrow}>Moderation</p>
          <h1 style={styles.title}>Content Moderation</h1>
        </div>
      </div>

      <div className="moderation-body">
        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(tab === 'comments' ? styles.tabActive : {}) }} onClick={() => setTab('comments')}>
            Comments ({comments.length})
          </button>
          <button style={{ ...styles.tab, ...(tab === 'pending' ? styles.tabActive : {}) }} onClick={() => setTab('pending')}>
            Pending ({posts.filter(p => p.status === 'pending').length})
          </button>
          <button style={{ ...styles.tab, ...(tab === 'posts' ? styles.tabActive : {}) }} onClick={() => setTab('posts')}>
            Published ({posts.filter(p => p.status === 'published').length})
          </button>
        </div>

        {loading ? <p style={styles.loadingText}>Loading...</p> : (
          <>
            {tab === 'comments' && (
              <div>
                <p style={styles.hint}>Review and remove offensive or inappropriate comments.</p>
                {comments.length === 0 ? (
                  <div className="empty-state"><p>No comments yet.</p></div>
                ) : (
                  <div style={styles.table}>
                    {comments.map(c => (
                      <div key={c.id} style={styles.commentRow}>
                        <div style={styles.commentMeta}>
                          <span style={styles.commentAuthor}>{c.profiles?.display_name}</span>
                          <span style={styles.commentDate}>{new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          <span style={styles.commentPostLink}>
                            on: <Link to={`/post/${c.posts?.id}`} style={{ color: 'var(--accent)', fontFamily: "'DM Mono', monospace", fontSize: '0.6rem' }}>{c.posts?.title}</Link>
                          </span>
                        </div>
                        <p style={styles.commentContent}>{c.content}</p>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteComment(c.id)}>Delete Comment</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === 'pending' && (
              <div>
                <p style={styles.hint}>Review and publish or reject submitted pieces.</p>
                {posts.filter(p => p.status === 'pending').length === 0 ? (
                  <div className="empty-state"><p>No pending submissions.</p></div>
                ) : (
                  <div style={styles.table}>
                    {posts.filter(p => p.status === 'pending').map(p => (
                      <div key={p.id} style={styles.postRow}>
                        <div style={{ flex: 1 }}>
                          <div style={styles.postTitle}>{p.title}</div>
                          <div style={styles.postMeta}>
                            {p.profiles?.display_name} · {'the feed'} ·
                            {p.tag && <span> {p.tag} ·</span>}
                            {p.read_time && <span> {p.read_time} min read</span>}
                          </div>
                          {p.excerpt && <p style={styles.excerpt}>{p.excerpt}</p>}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <button className="btn btn-sm" style={{ background: 'var(--success)', color: '#fff', border: 'none' }} onClick={() => publishPost(p.id)}>Publish</button>
                          <button className="btn btn-sm btn-danger" onClick={() => rejectPost(p.id)}>Reject</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === 'posts' && (
              <div>
                <p style={styles.hint}>Remove posts that violate community standards.</p>
                {posts.length === 0 ? (
                  <div className="empty-state"><p>No published posts yet.</p></div>
                ) : (
                  <div style={styles.table}>
                    {posts.map(p => (
                      <div key={p.id} style={styles.postRow}>
                        <div style={{ flex: 1 }}>
                          <div style={styles.postTitle}>{p.title}</div>
                          <div style={styles.postMeta}>{p.profiles?.display_name} · {p.section} · <span className={`status-badge status-${p.status}`}>{p.status}</span></div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-sm"
                            style={{ background: p.featured ? 'var(--accent)' : 'none', color: p.featured ? '#fff' : 'var(--muted)', border: '1px solid var(--rule)' }}
                            onClick={() => toggleFeatured(p)}
                            title={p.featured ? 'Remove from featured' : 'Set as featured piece'}
                          >
                            {p.featured ? '◈ Featured' : '◇ Feature'}
                          </button>
                          <Link to={`/post/${p.id}`} className="btn btn-sm btn-ghost">View</Link>
                          <button className="btn btn-sm btn-danger" onClick={() => deletePost(p.id)}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const styles = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  header: { padding: '2.5rem 3rem', borderBottom: '1px solid var(--rule)', background: 'var(--cream)' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.5rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.8rem', fontWeight: 700 },
  body: { padding: '2rem 3rem' },
  tabs: { display: 'flex', borderBottom: '1px solid var(--rule)', marginBottom: '2rem' },
  tab: { padding: '0.75rem 1.5rem', background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-1px', fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', cursor: 'pointer', transition: 'all 0.2s' },
  tabActive: { color: 'var(--ink)', borderBottomColor: 'var(--ink)' },
  loadingText: { fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', color: 'var(--muted)' },
  hint: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)', marginBottom: '1.5rem', letterSpacing: '0.04em' },
  table: { display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)' },
  commentRow: { background: 'var(--paper)', padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  commentMeta: { display: 'flex', gap: '1rem', alignItems: 'center' },
  commentAuthor: { fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', fontWeight: 500 },
  commentDate: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)' },
  commentPostLink: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)' },
  commentContent: { fontSize: '0.9rem', lineHeight: 1.65, color: 'var(--ink)' },
  postRow: { background: 'var(--paper)', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  postTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' },
  postMeta: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' },
  excerpt: { fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.6, marginTop: '0.5rem', fontStyle: 'italic' },
}
